import { useMemo } from 'react';
import { Flower2 } from 'lucide-react';
import { useDashboard } from '../../../context/DashboardContext';
import { collectNowBlooming } from '../../../utils/bloomUtils';
import { BLOOM_AUTOSTART_KEY } from '../../../constants/bloomAutostart';

/**
 * NowBloomingCard — „Jetzt blüht" (#270 / #609)
 *
 * State-unabhängige, ruhige Freude-/Orientierungskarte: nennt die gerade
 * blühenden Arten des Gartens mit Farbtupfern. Erscheint nur, wenn mindestens
 * eine Pflanze blüht (sonst gar nichts — kein leerer Platzhalter). Keine
 * Aufgaben-Mechanik, kein Datum, kein Zähler-Badge (Pflegekalender-Grundsatz).
 *
 * Klick öffnet den Editor im Blüh-Modus des aktuellen Monats (Signal über
 * sessionStorage, das MainLayoutContent beim Mount konsumiert, #633).
 */

const MAX_NAMES = 3;

const NowBloomingCard = () => {
  const { placedPlants, allPlants, setActiveView } = useDashboard();

  const blooming = useMemo(
    () => collectNowBlooming(placedPlants, allPlants, new Date().getMonth()),
    [placedPlants, allPlants]
  );

  if (blooming.length === 0) return null;

  const names = blooming.slice(0, MAX_NAMES).map((b) => b.name);
  const overflow = blooming.length - names.length;
  const summary = names.join(', ') + (overflow > 0 ? ` +${overflow}` : '');

  const openBloomMode = () => {
    try {
      sessionStorage.setItem(BLOOM_AUTOSTART_KEY, '1');
    } catch {
      // sessionStorage nicht verfügbar — Editor öffnet dann ohne Auto-Blüh-Modus
    }
    setActiveView('editor');
  };

  return (
    <section className="rounded-xl bg-white p-6 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Flower2 className="h-4 w-4 text-pink-500" aria-hidden="true" />
        <h2 className="text-base font-bold text-gray-900">Jetzt blüht</h2>
      </div>

      <div className="flex items-center gap-2">
        <span className="flex shrink-0 items-center gap-1" aria-hidden="true">
          {blooming.slice(0, MAX_NAMES + 1).map((b) => (
            <span
              key={b.id}
              className="inline-block h-3 w-3 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: b.color || '#D1D5DB' }}
            />
          ))}
        </span>
        <p className="text-sm text-gray-700">{summary}</p>
      </div>

      <button
        type="button"
        onClick={openBloomMode}
        className="mt-4 block text-[13px] text-emerald-600 hover:text-emerald-700"
      >
        Im Garten ansehen →
      </button>
    </section>
  );
};

export default NowBloomingCard;
